import React, { JSX, useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useBoards } from "../../contexts/BoardContext";
import { processEntryImage } from "../../lib/imageUtils";
import "./GlobalPostModal.css";


type GlobalPostModalProps = {
  open: boolean;
  onClose: () => void;
  onPostSuccess?: () => void;
};


export default function GlobalPostModal({ open, onClose, onPostSuccess }: GlobalPostModalProps): JSX.Element | null {
  const { user } = useAuth();
  const { boards, onNewEntry, loading: boardsLoading } = useBoards();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isBoardDropdownOpen, setIsBoardDropdownOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form and select all boards when modal opens
  useEffect(() => {
    if (open) {
      // Reset form fields
      setTitle("");
      setContent("");
      setImageFile(null);
      setImagePreview(null);
      // Select all boards by default when opening
      if (boards.length > 0) {
        setSelectedBoardIds(boards.map(board => board.id));
      }
    }
  }, [open, boards]);

  // Clean up object URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Alias for backwards compatibility with template
  const fetchingBoards = boardsLoading;

  if (!open) return null;

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      setImagePreview(URL.createObjectURL(originalFile));
      
      setIsProcessingImage(true);
      try {
        const processedFile = await processEntryImage(originalFile);
        setImageFile(processedFile);
      } catch (error) {
        console.error("Image processing error:", error);
        alert("Failed to process this image. Please try a different photo.");
        handleRemoveImage();
      } finally {
        setIsProcessingImage(false);
      }
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const toggleBoardSelection = (boardId: string) => {
    setSelectedBoardIds(prev =>
      prev.includes(boardId)
        ? prev.filter(id => id !== boardId)
        : [...prev, boardId]
    );
  };

  const handlePost = async () => {
    if (!user || selectedBoardIds.length === 0) return;

    try {
      setLoading(true);

      // Upload image once before creating entries
      let sharedImageUrl: string | null = null;
      if (imageFile) {
        // Always use .jpg since processEntryImage converts to JPEG
        const fileName = `${user.id}/${Date.now()}-${Math.random()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('entry-images')
          .upload(fileName, imageFile);

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
        } else {
          const { data: urlData } = supabase.storage
            .from('entry-images')
            .getPublicUrl(fileName);
          sharedImageUrl = urlData.publicUrl;
        }
      }

      // Create entries for each selected board
      for (const boardId of selectedBoardIds) {
        const { data: insertedData, error: insertError } = await supabase
          .from('entries')
          .insert({
            user_id: user.id,
            board_id: boardId,
            title: title || null,
            content: content,
            image_url: sharedImageUrl,
            reactions_count: 0
          })
          .select()
          .single();

        if (insertError) {
          console.error(`Error creating entry for board ${boardId}:`, insertError);
        } else if (insertedData) {
          const optimisticEntry = {
            ...insertedData,
            reactions_count: 0,
            user_has_reacted: false,
            author: {
              display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || "Me",
              avatar_url: user.user_metadata?.avatar_url || null
            }
          };

          onNewEntry(optimisticEntry);
        }
      }

      onClose();
      if (onPostSuccess) onPostSuccess();

    } catch (error) {
      console.error('Error posting entries:', error);
      alert('Failed to post entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isPostDisabled = content.trim() === "" || selectedBoardIds.length === 0 || loading || isProcessingImage;

  // Format selection label
  let selectionLabel = "Select boards";
  if (selectedBoardIds.length > 0 && selectedBoardIds.length === boards.length) {
    selectionLabel = "All boards selected";
  } else if (selectedBoardIds.length === 1) {
    selectionLabel = boards.find(b => b.id === selectedBoardIds[0])?.name || "1 board selected";
  } else if (selectedBoardIds.length > 1) {
    selectionLabel = `${selectedBoardIds.length} boards selected`;
  }

  return (
    <div className="global-post-modal">
      <div className="overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">Post Gratitude</h2>
            <button className="modal-close-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Board Selector */}
          <div className="board-selector">
            <label className="board-select-label">Post to Board(s)</label>
            <div style={{ position: 'relative' }}>
              <button
                className={`board-select-button ${selectedBoardIds.length === 0 ? 'placeholder' : ''}`}
                onClick={() => setIsBoardDropdownOpen(!isBoardDropdownOpen)}
              >
                <span>{selectionLabel}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {isBoardDropdownOpen && (
                <div className="board-dropdown">
                  {boards.length === 0 ? (
                    <div style={{ padding: '12px', color: '#64748b', fontSize: '14px' }}>
                      {fetchingBoards ? 'Loading boards...' : 'No boards found.'}
                    </div>
                  ) : (
                    <>
                      {/* Optional: Select All / None actions could go here */}
                      {boards.map(board => {
                        const isSelected = selectedBoardIds.includes(board.id);
                        return (
                          <div
                            key={board.id}
                            className={`board-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleBoardSelection(board.id)}
                          >
                            {/* Checkbox Visual */}
                            <div style={{
                              width: 18,
                              height: 18,
                              borderRadius: 4,
                              border: `2px solid ${isSelected ? 'var(--accent-primary)' : '#cbd5e1'}`,
                              backgroundColor: isSelected ? 'var(--accent-primary)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: 4,
                              flexShrink: 0
                            }}>
                              {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              )}
                            </div>
                            <span>{board.name}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
            {isBoardDropdownOpen && (
              <div
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5 }}
                onClick={() => setIsBoardDropdownOpen(false)}
              />
            )}
          </div>

          {/* Title Input */}
          <input
            type="text"
            className="title-input"
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
          />

          {/* Content Textarea */}
          <textarea
            className="content-textarea"
            placeholder="What are you grateful for today?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            disabled={loading}
          />

          {/* Upload Photo Area */}
          <div
            className="upload-area"
            onClick={() => fileInputRef.current?.click()}
            style={{
              backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              border: imagePreview ? '2px solid transparent' : '2px dashed #e2e8f0'
            }}
          >
            {!imagePreview && (
              <>
                <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="upload-text">Upload an image</span>
              </>
            )}
            {imagePreview && (
              <button
                className="remove-image-button"
                onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className="post-button"
              onClick={handlePost}
              disabled={isPostDisabled}
            >
              {loading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
